import { useRouter } from "vue-router";
import type { LocationQueryRaw } from "vue-router";
import type { ModalRoute } from "@/types/modal.types";

export function useModalNavigation() {
	const router = useRouter();

	// Define which parameters belong to each modal
	const modalParams: Record<ModalRoute, string[]> = {
		"create-note": [],
		"task-request": [],
		"user-details": ["userDetailsId"],
		"batch-add-plantings": [],
	};

	// Get the currently open modal
	function getCurrentModal(): ModalRoute | null {
		const modalParam = router.currentRoute.value.query.modal;
		const modalId = Array.isArray(modalParam) ? modalParam[0] : modalParam;

		if (!modalId || typeof modalId !== "string") return null;

		return modalId in modalParams ? (modalId as ModalRoute) : null;
	}

	// Remove specific keys from query object, filtering out null/undefined values
	function removeKeys(
		query: LocationQueryRaw,
		keysToRemove: string[],
	): LocationQueryRaw {
		const keysSet = new Set(keysToRemove);
		return Object.fromEntries(
			Object.entries(query).filter(
				([key, value]) => !keysSet.has(key) && value != null,
			),
		);
	}

	async function viewGlobalModal(
		modalId: ModalRoute,
		params: Record<string, string> = {},
	) {
		const currentQuery = { ...router.currentRoute.value.query };
		const currentModal = getCurrentModal();

		// Remove current modal's params, then add new modal and its params
		const keysToRemove = currentModal ? modalParams[currentModal] : [];
		const cleanQuery = removeKeys(currentQuery, keysToRemove);

		const nextQuery = {
			...cleanQuery,
			modal: modalId,
			...params,
		};

		await router.replace({ query: nextQuery });
	}

	async function closeGlobalModal() {
		const currentQuery = { ...router.currentRoute.value.query };
		const currentModal = getCurrentModal();

		if (!currentModal) {
			// No modal is open, do nothing
			return;
		}

		// Remove current modal's params and the modal flag
		const keysToRemove = ["modal", ...modalParams[currentModal]];
		const nextQuery = removeKeys(currentQuery, keysToRemove);

		await router.replace({ query: nextQuery });
	}

	return {
		viewGlobalModal,
		closeGlobalModal,
	};
}
